import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Zap, 
  Users, 
  MapPin, 
  Search,
  Plus,
  ArrowRight,
  LogOut,
  TrendingUp,
  Leaf
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Communities = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async (location = '') => {
    try {
      const params = location ? { location } : {};
      const response = await axios.get(`${API_URL}/api/communities`, { params });
      setCommunities(response.data);
    } catch (error) {
      console.error('Error fetching communities:', error);
      toast.error('Erreur lors du chargement des communautés');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchCommunities(searchQuery);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Déconnexion réussie');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo.png" alt="Green Teva" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-bold text-primary">Green Teva</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <Link to="/communities" className="nav-link active">Communautés</Link>
              <Link to="/simulator" className="nav-link">Simulateur</Link>
            </div>
            
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  <div className="hidden sm:block text-right">
                    <p className="text-sm font-medium text-foreground">{user?.name}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={handleLogout}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => navigate('/login')}
                  className="btn-primary"
                  data-testid="login-btn"
                >
                  Connexion
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Communautés d'autoconsommation
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Trouvez une communauté près de chez vous ou créez la vôtre
            </p>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher par ville ou code postal..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-24 py-6 text-lg input-green"
                data-testid="search-input"
              />
              <Button 
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 btn-primary"
                data-testid="search-btn"
              >
                Rechercher
              </Button>
            </div>
          </form>

          {/* Create Community CTA */}
          <div className="mb-8 flex justify-center">
            <Button
              onClick={() => {
                if (isAuthenticated) {
                  navigate('/communities/create');
                } else {
                  toast.error('Connectez-vous pour créer une communauté');
                  navigate('/login');
                }
              }}
              variant="outline"
              className="btn-secondary"
              data-testid="create-community-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Créer ma communauté
            </Button>
          </div>

          {/* Communities Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="loader" />
            </div>
          ) : communities.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Aucune communauté trouvée
              </h3>
              <p className="text-muted-foreground mb-6">
                Soyez le premier à créer une communauté dans votre région !
              </p>
              <Button 
                onClick={() => navigate('/communities/create')}
                className="btn-primary"
                data-testid="create-first-community-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Créer une communauté
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {communities.map((community) => (
                <Card 
                  key={community.id} 
                  className="community-card overflow-hidden"
                  data-testid={`community-card-${community.id}`}
                >
                  <div className="h-32 bg-gradient-to-br from-primary/20 to-accent/20 relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Zap className="w-16 h-16 text-primary/30" />
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      {community.name}
                    </h3>
                    
                    <div className="flex items-center gap-2 text-muted-foreground mb-3">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{community.location}</span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {community.description}
                    </p>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-muted/50 rounded-xl">
                      <div className="text-center">
                        <p className="text-lg font-bold text-primary">{community.member_count}</p>
                        <p className="text-xs text-muted-foreground">Membres</p>
                      </div>
                      <div className="text-center border-x border-border">
                        <p className="text-lg font-bold text-foreground">{community.price_per_kwh}€</p>
                        <p className="text-xs text-muted-foreground">par kWh</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-foreground">{community.max_members}</p>
                        <p className="text-xs text-muted-foreground">Max</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm mb-4">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <TrendingUp className="w-4 h-4" />
                        <span>{community.total_production_kwh?.toFixed(0) || 0} kWh produits</span>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => navigate(`/communities/${community.id}`)}
                      className="w-full btn-primary"
                      data-testid={`view-community-btn-${community.id}`}
                    >
                      Voir la communauté
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Communities;
